import type { ChainId } from '#lib/models/index.js'

export type ChainConfig = {
	id: ChainId
	listed: Date | false
	name: string
	slug: string
	tokens?: Nullable<
		| {
				gasToken: Nullable<string>
				priceToken: Nullable<string>
				stakeToken: Nullable<string>
		  }
		| string
	>
}

/* eslint sort-keys: ["error", "asc", { caseSensitive: true, natural: true }] */
const CHAINS_CONFIG_WITHOUT_IDS: Record<ChainId, Omit<ChainConfig, 'id'>> = {
	algorand_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Algorand',
		slug: 'algorand',
		tokens: 'ALGO',
	},
	allora_chain: {
		listed: false,
		name: 'Allora',
		slug: 'allora',
	},
	aptos_chain: {
		listed: new Date('2024-05-27T00:00:00Z'),
		name: 'Aptos',
		slug: 'aptos',
		tokens: 'APT',
	},
	arbitrum_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Arbitrum',
		slug: 'arbitrum',
		tokens: {
			gasToken: 'ETH',
			priceToken: 'ARB',
			stakeToken: 'ARB',
		},
	},
	arc_chain: {
		listed: false,
		name: 'Arc',
		slug: 'arc',
	},
	avalanche_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Avalanche',
		slug: 'avalanche',
		tokens: 'AVAX',
	},
	aztec_chain: {
		listed: false,
		name: 'Aztec',
		slug: 'aztec',
	},
	babylon_chain: {
		listed: false,
		name: 'Babylon',
		slug: 'babylon',
	},
	base_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Base',
		slug: 'base',
		tokens: {
			gasToken: 'ETH',
			priceToken: null,
			stakeToken: null,
		},
	},
	beam_chain: {
		listed: false,
		name: 'Beam',
		slug: 'beam',
	},
	bitcoin_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Bitcoin',
		slug: 'bitcoin',
		tokens: 'BTC',
	},
	bnb_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'BNB Chain',
		slug: 'bnb-chain',
		tokens: 'BNB',
	},
	bsquared_chain: {
		listed: false,
		name: 'B² Network',
		slug: 'bsquared',
	},
	bsv_chain: {
		listed: new Date('2025-08-04T00:00:00Z'),
		name: 'BSV Blockchain',
		slug: 'bsv',
		tokens: 'BSV',
	},
	cardano_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Cardano',
		slug: 'cardano',
		tokens: 'ADA',
	},
	cosmos_chain: {
		listed: false,
		name: 'Cosmos',
		slug: 'cosmos',
	},
	creditcoin_chain: {
		listed: false,
		name: 'Creditcoin',
		slug: 'creditcoin',
	},
	cronos_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Cronos Network',
		slug: 'cronos',
		tokens: 'CRO',
	},
	dusk_chain: {
		listed: false,
		name: 'Dusk',
		slug: 'dusk',
	},
	ecash_chain: {
		listed: new Date('2024-03-28T00:00:00Z'),
		name: 'eCash',
		slug: 'ecash',
		tokens: 'XEC',
	},
	ethereum_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Ethereum',
		slug: 'ethereum',
		tokens: 'ETH',
	},
	etherlink_chain: {
		listed: new Date('2025-10-16T00:00:00Z'),
		name: 'Etherlink',
		slug: 'etherlink',
		tokens: {
			gasToken: 'XTZ',
			priceToken: null,
			stakeToken: null,
		},
	},
	flare_chain: {
		listed: false,
		name: 'Flare',
		slug: 'flare',
	},
	fogo_chain: {
		listed: new Date('2025-12-16T00:00:00Z'),
		name: 'Fogo',
		slug: 'fogo',
		tokens: 'FOGO',
	},
	gravity_chain: {
		listed: false,
		name: 'Gravity',
		slug: 'gravity',
	},
	hedera_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Hedera',
		slug: 'hedera',
		tokens: 'HBAR',
	},
	hyperliquid_chain: {
		listed: false,
		name: 'Hyperliquid',
		slug: 'hyperliquid',
	},
	icp_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'ICP',
		slug: 'icp',
		tokens: 'ICP',
	},
	immutable_x_chain: {
		listed: false,
		name: 'Immutable X',
		slug: 'immutable-x',
	},
	ink_chain: {
		listed: false,
		name: 'Ink',
		slug: 'ink',
	},
	iotex_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'IoTeX',
		slug: 'iotex',
		tokens: 'IOTX',
	},
	kaia_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Kaia',
		slug: 'kaia',
		tokens: 'KAIA',
	},
	kava_chain: {
		listed: false,
		name: 'Kava',
		slug: 'kava',
	},
	keeta_mainnet_chain: {
		listed: new Date('2025-09-22T00:00:00Z'),
		name: 'Keeta Mainnet',
		slug: 'keeta-mainnet',
		tokens: 'KTA',
	},
	keeta_testnet_chain: {
		listed: new Date('2025-05-08T00:00:00Z'),
		name: 'Keeta Testnet',
		slug: 'keeta-testnet',
		tokens: {
			gasToken: null,
			priceToken: null,
			stakeToken: 'KTA',
		},
	},
	kite_ai_chain: {
		listed: false,
		name: 'KITE AI',
		slug: 'kite-ai',
	},
	kusama_chain: {
		listed: new Date('2025-05-12T00:00:00Z'),
		name: 'Kusama',
		slug: 'kusama',
		tokens: 'KSM',
	},
	lens_chain: {
		listed: false,
		name: 'Lens Chain',
		slug: 'lens',
	},
	linea_chain: {
		listed: false,
		name: 'Linea',
		slug: 'linea',
	},
	mantle_chain: {
		listed: false,
		name: 'Mantle',
		slug: 'mantle',
	},
	megaeth_chain: {
		listed: false,
		name: 'MegaETH',
		slug: 'megaeth',
	},
	monad_chain: {
		listed: false,
		name: 'Monad',
		slug: 'monad',
	},
	multiversx_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'MultiversX',
		slug: 'multiversx',
		tokens: 'EGLD',
	},
	n1_chain: {
		listed: false,
		name: 'N1',
		slug: 'n1',
	},
	near_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'NEAR',
		slug: 'near',
		tokens: 'NEAR',
	},
	neo_chain: {
		listed: false,
		name: 'Neo',
		slug: 'neo',
	},
	optimism_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Optimism',
		slug: 'optimism',
		tokens: {
			gasToken: 'ETH',
			priceToken: 'OP',
			stakeToken: 'OP',
		},
	},
	pharos_chain: {
		listed: false,
		name: 'Pharos',
		slug: 'pharos',
	},
	plasma_chain: {
		listed: false,
		name: 'Plasma',
		slug: 'plasma',
	},
	plume_chain: {
		listed: false,
		name: 'Plume',
		slug: 'plume',
	},
	polkadot_chain: {
		listed: new Date('2025-04-25T00:00:00Z'),
		name: 'Polkadot',
		slug: 'polkadot',
		tokens: 'DOT',
	},
	polygon_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Polygon',
		slug: 'polygon',
		tokens: 'POL',
	},
	quranium_chain: {
		listed: false,
		name: 'Quranium',
		slug: 'quranium',
	},
	redbelly_chain: {
		listed: new Date('2025-03-26T00:00:00Z'),
		name: 'Redbelly',
		slug: 'redbelly',
		tokens: 'RBNT',
	},
	rise_chain: {
		listed: false,
		name: 'RISE',
		slug: 'rise',
	},
	ritual_chain: {
		listed: false,
		name: 'Ritual',
		slug: 'ritual',
	},
	ronin_chain: {
		listed: false,
		name: 'Ronin',
		slug: 'ronin',
	},
	sei_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Sei',
		slug: 'sei',
		tokens: 'SEI',
	},
	solana_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Solana',
		slug: 'solana',
		tokens: 'SOL',
	},
	solayer_chain: {
		listed: false,
		name: 'Solayer',
		slug: 'solayer',
	},
	somnia_chain: {
		listed: new Date('2025-09-09T00:00:00Z'),
		name: 'Somnia',
		slug: 'somnia',
		tokens: 'SOMI',
	},
	sonic_chain: {
		listed: new Date('2025-01-08T00:00:00Z'),
		name: 'Sonic',
		slug: 'sonic',
		tokens: 'S',
	},
	soon_chain: {
		listed: false,
		name: 'Soon',
		slug: 'soon',
	},
	starknet_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Starknet',
		slug: 'starknet',
		tokens: 'STRK',
	},
	stellar_chain: {
		listed: new Date('2024-05-21T00:00:00Z'),
		name: 'Stellar',
		slug: 'stellar',
		tokens: 'XLM',
	},
	sui_chain: {
		listed: new Date('2025-06-23T00:00:00Z'),
		name: 'Sui',
		slug: 'sui',
		tokens: 'SUI',
	},
	tezos_chain: {
		listed: new Date('2025-10-16T00:00:00Z'),
		name: 'Tezos',
		slug: 'tezos',
		tokens: 'XTZ',
	},
	tezos_ecosystem_chain: {
		listed: new Date('2025-10-16T00:00:00Z'),
		name: 'Tezos Ecosystem',
		slug: 'tezos-ecosystem',
		tokens: 'XTZ',
	},
	theta_chain: {
		listed: false,
		name: 'Theta',
		slug: 'theta',
	},
	ton_chain: {
		listed: new Date('2025-08-26T00:00:00Z'),
		name: 'TON',
		slug: 'ton',
		tokens: 'GRAM',
	},
	tron_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'TRON',
		slug: 'tron',
		tokens: 'TRX',
	},
	vechain_chain: {
		listed: false,
		name: 'VeChain',
		slug: 'vechain',
	},
	wanchain_chain: {
		listed: new Date('2024-04-07T00:00:00Z'),
		name: 'Wanchain',
		slug: 'wanchain',
		tokens: 'WAN',
	},
	waterfall_chain: {
		listed: new Date('2023-08-01T00:00:00Z'),
		name: 'Waterfall',
		slug: 'waterfall',
	},
	world_chain: {
		listed: false,
		name: 'World Chain',
		slug: 'world',
	},
	xdc_chain: {
		listed: false,
		name: 'XDC Network',
		slug: 'xdc',
	},
	xrpl_chain: {
		listed: false,
		name: 'XRP Ledger',
		slug: 'xrpl',
	},
	zksync_chain: {
		listed: false,
		name: 'ZKsync',
		slug: 'zksync',
	},
}

export const CHAINS_CONFIG: Record<ChainId, ChainConfig> = Object.fromEntries(
	Object.entries(CHAINS_CONFIG_WITHOUT_IDS).map(([id, config]) => [id, { id, ...config }])
) as { [K in keyof typeof CHAINS_CONFIG_WITHOUT_IDS]: ChainConfig } satisfies Record<
	ChainId,
	ChainConfig
>

export const LISTED_CHAINS_CONFIG: Record<ChainId, ChainConfig> = Object.fromEntries(
	Object.entries(CHAINS_CONFIG).filter(([, config]) => !!config.listed)
) as { [K in keyof typeof CHAINS_CONFIG]: ChainConfig } satisfies Record<ChainId, ChainConfig>

export const CHAINS_CONFIG_BY_SLUG = Object.fromEntries(
	Object.entries(CHAINS_CONFIG).map(([, config]) => [config.slug, config])
)

export const LISTED_CHAINS_CONFIG_BY_SLUG = Object.fromEntries(
	Object.entries(LISTED_CHAINS_CONFIG).map(([, config]) => [config.slug, config])
)

export const CHAINS_LIST = Object.values(CHAINS_CONFIG).sort((a, b) => {
	if (!a.listed) return 1
	if (!b.listed) return -1

	return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
})

export const LISTED_CHAINS = CHAINS_LIST.filter(c => !!c.listed)

export const LISTED_CHAINS_IDS = LISTED_CHAINS.map(({ id }) => id)

export const LISTED_CHAINS_WITH_NATIVE_TOKENS = LISTED_CHAINS.map(c => ({
	id: c.id,
	name: c.name,
	slug: c.slug,
	token: typeof c.tokens === 'string' ? c.tokens : (c.tokens?.priceToken ?? null),
}))
	.filter(
		(c): c is { id: ChainId; name: string; slug: string; token: string } =>
			c.token != null && c.id !== 'keeta_testnet_chain' && c.id !== 'tezos_ecosystem_chain'
	)
	.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

export const LISTED_CHAINS_WITH_NATIVE_TOKENS_BY_SLUG = Object.fromEntries(
	LISTED_CHAINS_WITH_NATIVE_TOKENS.map(c => [c.slug, c])
) as Record<string, { id: ChainId; name: string; slug: string; token: string }>
